import React, { useContext, useMemo, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { CircularProgress } from '@mui/material';
import { LangContext } from '../../../context/LangContext';
import { ThemeContext } from '../../../context/ThemeContext';
import { Notification } from '../../UI/Notifications';
import { addCommentApi } from '../../../utils/api/Lead/Comments/addCommentApi';
import { handleError } from '../../../utils/custom/errorHandler';
import styles from '../../../styles/components/Modal/Lead/LeadCommentModal.module.css';

const translations = {
    ru: {
        createCommentTitle: (leadName) => `Создать комментарий для лида ${leadName}`,
        placeholder: 'Напишите комментарий...',
        create: 'Создать',
        creating: 'Создание...',
        cancel: 'Отмена',
        emptyCommentError: 'Пожалуйста, введите текст комментария.',
    },
    en: {
        createCommentTitle: (leadName) => `Create comment for lead ${leadName}`,
        placeholder: 'Write a comment...',
        create: 'Create',
        creating: 'Creating...',
        cancel: 'Cancel',
        emptyCommentError: 'Please enter a comment.',
    },
};

export const LeadCommentModal = ({ lead, role, onClose, onSuccess }) => {
    const { langSelected } = useContext(LangContext);
    const { theme } = useContext(ThemeContext);
    const currentTranslation = translations[langSelected] || translations.ru;

    const [commentText, setCommentText] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState({});

    const leadName = useMemo(() => {
        const fullName = `${lead?.first_name || ''} ${lead?.last_name || ''}`.trim();
        return fullName || `#${lead?.id ?? ''}`;
    }, [lead]);

    const commentErrorMessage = Array.isArray(errors.comment) ? errors.comment[0] : errors.comment;

    const handleSubmit = async () => {
        const trimmedComment = commentText.trim();

        if (!trimmedComment) {
            setErrors({ comment: currentTranslation.emptyCommentError });
            return;
        }

        setIsSubmitting(true);
        setErrors({});

        try {
            await addCommentApi(lead.id, trimmedComment, role);
            await onSuccess();
            onClose();
        } catch (error) {
            handleError(error.response?.data || error, setErrors);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className={styles.modalOverlay} onClick={onClose}>
            <div
                className={`${styles.modalContent} ${theme === 'dark' ? styles.darkContent : ''}`}
                onClick={(event) => event.stopPropagation()}
            >
                <div className={styles.modalHeader}>
                    <h2 className={styles.modalTitle}>{currentTranslation.createCommentTitle(leadName)}</h2>
                    <button className={styles.closeButton} onClick={onClose} type="button" aria-label="Close">
                        <CloseIcon />
                    </button>
                </div>

                <div className={styles.modalBody}>
                    {(errors.general || commentErrorMessage) && (
                        <Notification message={commentErrorMessage || errors.general} type="error" />
                    )}
                    <textarea
                        className={`${styles.textarea} ${theme === 'dark' ? styles.darkTextarea : ''}`}
                        value={commentText}
                        onChange={(event) => {
                            setCommentText(event.target.value);
                            if (errors.comment || errors.general) {
                                setErrors({});
                            }
                        }}
                        placeholder={currentTranslation.placeholder}
                        rows={6}
                        disabled={isSubmitting}
                    />
                </div>

                <div className={styles.modalFooter}>
                    <button
                        type="button"
                        className={`${styles.secondaryButton} ${theme === 'dark' ? styles.darkSecondaryButton : ''}`}
                        onClick={onClose}
                        disabled={isSubmitting}
                    >
                        {currentTranslation.cancel}
                    </button>
                    <button
                        type="button"
                        className={`${styles.primaryButton} ${theme === 'dark' ? styles.darkPrimaryButton : ''}`}
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? <CircularProgress size={18} color="inherit" /> : currentTranslation.create}
                        {isSubmitting ? currentTranslation.creating : null}
                    </button>
                </div>
            </div>
        </div>
    );
};
